import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from '../configuration/tokenHandling/prepareHeaders';
import { config } from '../config';
import { isRelevantDevice, removeDuplicatesById } from './assetsApiUtils';
import type { RootState } from '../configuration/setup/store';
import { useAppSelector } from '../configuration/setup/hooks';

export interface Asset {
    id: string;
    name: string;
    type: string;
    status: string;
    identification: string | undefined;
    identification_type: string | undefined;
}

export interface Device {
    id: string;
    identification: string;
    type: string;
}

interface Associations {
    id: string;
    device_id: string;
    asset_id: string;
    _embedded:
        | {
              device: Device | undefined;
              asset: Asset | undefined;
          }
        | undefined;
}

interface PageResponse {
    items: Associations[];
    _links?: {
        next?: {
            href?: string;
        };
    };
}

export const assetsApi = createApi({
    reducerPath: 'assetsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.ASSETS_API,
        prepareHeaders,
    }),
    endpoints: builder => ({
        fetchAllRelevantAssets: builder.query<Asset[], void>({
            queryFn: async (_args, _queryApi, _extraOptions, baseQuery) => {
                let allRelevantAssets: Asset[] = [];
                let currentUrl: string | undefined = '/associations?embed=(asset,device)'; // Start with the base URL

                try {
                    while (currentUrl) {
                        const result = await baseQuery({
                            url: currentUrl,
                        });

                        if (result.error) {
                            console.error('fetchAllAssets: Error fetching page', currentUrl, result.error);
                            // Propagate the error to RTK Query
                            throw { message: 'Failed to fetch page', details: result.error };
                        }

                        if (!result.data) {
                            console.warn('fetchAllAssets: No data received for page', currentUrl);
                            break;
                        }

                        // Assuming the structure matches PageResponse after baseQuery transforms/parsing
                        const pageData = result.data as PageResponse;
                        const items = pageData.items || [];
                        const nextLink = pageData._links?.next?.href;

                        const relevantAssets = items
                            .filter(
                                it =>
                                    it._embedded?.device && isRelevantDevice(it._embedded.device) && it._embedded.asset
                            )
                            .map(it => it._embedded?.asset)
                            .filter(it => it !== undefined);

                        allRelevantAssets = allRelevantAssets.concat(relevantAssets);

                        // Determine the *next* URL relative to the base URL
                        if (nextLink) {
                            // The nextLink is often absolute. We only need the path and query params.
                            try {
                                const urlObject = new URL(nextLink);
                                currentUrl = urlObject.pathname + urlObject.search;
                            } catch (e) {
                                console.error('fetchAllAssets: Could not parse nextLink URL', nextLink, e);
                                currentUrl = undefined; // Stop if URL is invalid
                            }
                        } else {
                            currentUrl = undefined; // No more pages
                        }
                    }

                    return { data: removeDuplicatesById(allRelevantAssets) };
                    // biome-ignore lint/suspicious/noExplicitAny: dont overcomplicate types
                } catch (error: any) {
                    console.error('fetchAllAssets: Process failed.', error);
                    // Return the error in the format RTK Query expects
                    // Ensure it's serializable if possible
                    const errorPayload = error.details || {
                        message: error.message || 'Unknown error during fetchAllAssets',
                    };
                    return { error: errorPayload };
                }
            },
        }),
    }),
});

export const { useFetchAllRelevantAssetsQuery } = assetsApi;

export const selectRelevantAssets = (state: RootState): Asset[] | undefined =>
    assetsApi.endpoints.fetchAllRelevantAssets.select()(state).data;
export const useRelevantAssets = () => useAppSelector(selectRelevantAssets);
